import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import { theme } from '../../../themes/theme'
import { AI_BOX_FILL, AI_BOX_STROKE, PAPER, withAlpha } from '../../styles/colors'
import { pointsToPx, pxToPoints, clampBox, MIN_BOX_POINTS } from '../../../util/redaction/annotationMath'

export const HANDLE_PX = 12
export const MIN_HANDLE_PX = 6
const MIN_DRAW_PX = 6

// Keep resize handles usable on thin boxes.
export function handleLayout(widthPx, heightPx) {
  const size = Math.max(MIN_HANDLE_PX, Math.min(HANDLE_PX, Math.floor(Math.min(widthPx, heightPx) / 3)))
  const showNS = heightPx >= 3 * HANDLE_PX
  const showEW = widthPx >= 3 * HANDLE_PX
  return { size, handles: HANDLES.filter(h => (h.key.length === 2 ? true : h.key === 'n' || h.key === 's' ? showNS : showEW)) }
}

const colors = {
  ai: { fill: AI_BOX_FILL, stroke: AI_BOX_STROKE },
  manual: { fill: withAlpha(theme.colors.primary, 0.25), stroke: theme.colors.primary },
  selected: theme.colors.primary
}

const Layer = styled.div`
  position: absolute;
  inset: 0;
  cursor: ${props => (props.tool === 'add' ? 'crosshair' : 'default')};
  user-select: none;
`

const Box = styled.div`
  position: absolute;
  box-sizing: border-box;
`

const Handle = styled.div`
  position: absolute;
  background: ${PAPER};
  border: 2px solid ${theme.colors.primary};
  border-radius: 2px;
  box-sizing: border-box;
`

const Label = styled.div`
  position: absolute;
  left: 0;
  top: 100%;
  margin-top: 4px;
  padding: 3px 8px;
  background: ${theme.colors.primary};
  color: ${PAPER};
  font-size: 11px;
  line-height: 14px;
  white-space: nowrap;
  border-radius: 3px;
  pointer-events: none;
`

const HANDLES = [
  { key: 'nw', x: 0, y: 0, cursor: 'nwse-resize' },
  { key: 'n', x: 0.5, y: 0, cursor: 'ns-resize' },
  { key: 'ne', x: 1, y: 0, cursor: 'nesw-resize' },
  { key: 'e', x: 1, y: 0.5, cursor: 'ew-resize' },
  { key: 'se', x: 1, y: 1, cursor: 'nwse-resize' },
  { key: 's', x: 0.5, y: 1, cursor: 'ns-resize' },
  { key: 'sw', x: 0, y: 1, cursor: 'nesw-resize' },
  { key: 'w', x: 0, y: 0.5, cursor: 'ew-resize' }
]

// Interactive redaction boxes for one PDF page.
function RedactionLayer({
  pageIndex,
  dims,
  boxes,
  tool = 'select',
  editable = true,
  selectedId = null,
  onSelect = () => {},
  onCreate = () => {},
  onUpdate = () => {}
}) {
  const ref = useRef(null)
  const drag = useRef(null)
  const [draft, setDraftState] = useState(null)
  const draftRef = useRef(null)

  function setDraft(next) {
    draftRef.current = typeof next === 'function' ? next(draftRef.current) : next
    setDraftState(draftRef.current)
  }

  const onPage = boxes.filter(b => b.page === pageIndex)

  function localPoint(e) {
    const rect = ref.current.getBoundingClientRect()
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  function onBackgroundMouseDown(e) {
    if (e.button !== 0) return
    if (e.target !== ref.current) return
    if (!editable || tool !== 'add') {
      onSelect(null)
      return
    }
    const p = localPoint(e)
    drag.current = { mode: 'draw', startX: p.x, startY: p.y }
    setDraft({ left: p.x, top: p.y, width: 0, height: 0 })
    e.preventDefault()
  }

  function beginMove(e, box) {
    if (e.button !== 0) return
    e.stopPropagation()
    onSelect(box.id)
    if (!editable) return
    const p = localPoint(e)
    const px = pointsToPx(box, dims.scale)
    drag.current = { mode: 'move', id: box.id, dx: p.x - px.left, dy: p.y - px.top, start: px, moved: false }
    setDraft(px)
    e.preventDefault()
  }

  function beginResize(e, box, handle) {
    if (e.button !== 0) return
    e.stopPropagation()
    onSelect(box.id)
    if (!editable) return
    const px = pointsToPx(box, dims.scale)
    drag.current = { mode: 'resize', id: box.id, handle, start: px, moved: false }
    setDraft(px)
    e.preventDefault()
  }

  useEffect(() => {
    function onMove(e) {
      const d = drag.current
      if (!d) return
      const p = localPoint(e)
      if (d.mode === 'draw') {
        setDraft({
          left: Math.min(p.x, d.startX),
          top: Math.min(p.y, d.startY),
          width: Math.abs(p.x - d.startX),
          height: Math.abs(p.y - d.startY)
        })
      } else if (d.mode === 'move') {
        d.moved = true
        setDraft(cur => ({ ...cur, left: p.x - d.dx, top: p.y - d.dy }))
      } else if (d.mode === 'resize') {
        d.moved = true
        const s = d.start
        const minPx = MIN_BOX_POINTS * dims.scale
        let { left, top } = s
        let right = s.left + s.width
        let bottom = s.top + s.height
        if (d.handle.x === 0) left = Math.min(p.x, right - minPx)
        if (d.handle.x === 1) right = Math.max(p.x, left + minPx)
        if (d.handle.y === 0) top = Math.min(p.y, bottom - minPx)
        if (d.handle.y === 1) bottom = Math.max(p.y, top + minPx)
        setDraft({ left, top, width: right - left, height: bottom - top })
      }
    }
    function onUp() {
      const d = drag.current
      if (!d) return
      drag.current = null
      const cur = draftRef.current
      setDraft(null)
      if (!cur) return
      if (d.mode === 'draw') {
        if (cur.width > MIN_DRAW_PX && cur.height > MIN_DRAW_PX) {
          onCreate(clampBox({ page: pageIndex, ...pxToPoints(cur, dims.scale) }, dims.pageWidth, dims.pageHeight))
        }
      } else if (d.moved) {
        const { x, y, w, h } = clampBox(pxToPoints(cur, dims.scale), dims.pageWidth, dims.pageHeight)
        onUpdate(d.id, { x, y, w, h })
      }
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [dims, pageIndex, onCreate, onUpdate])

  return (
    <Layer ref={ref} data-testid="redaction-layer" tool={editable ? tool : 'select'} onMouseDown={onBackgroundMouseDown}>
      {onPage.map(box => {
        const px = draft && drag.current?.id === box.id ? draft : pointsToPx(box, dims.scale)
        const selected = selectedId === box.id
        const palette = colors[box.origin === 'manual' ? 'manual' : 'ai']
        return (
          <Box
            key={box.id}
            data-testid="redaction-box"
            data-box-id={box.id}
            data-origin={box.origin}
            data-selected={selected ? 'true' : 'false'}
            role="button"
            aria-label={`Redaction box: ${box.reason || 'no reason'}`}
            aria-pressed={selected}
            tabIndex={0}
            title={box.reason}
            onMouseDown={e => beginMove(e, box)}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onSelect(box.id)
              }
            }}
            style={{
              left: px.left,
              top: px.top,
              width: px.width,
              height: px.height,
              background: palette.fill,
              border: `${selected ? 2 : 1.5}px solid ${selected ? colors.selected : palette.stroke}`,
              cursor: editable ? 'move' : 'pointer',
              zIndex: selected ? 2 : 1
            }}>
            {selected && editable && (
              <>
                {(() => {
                  const { size, handles } = handleLayout(px.width, px.height)
                  return handles.map(handle => (
                    <Handle
                      key={handle.key}
                      data-testid={`resize-handle-${handle.key}`}
                      onMouseDown={e => beginResize(e, box, handle)}
                      style={{
                        width: size,
                        height: size,
                        left: `calc(${handle.x * 100}% - ${size / 2}px)`,
                        top: `calc(${handle.y * 100}% - ${size / 2}px)`,
                        cursor: handle.cursor
                      }}
                    />
                  ))
                })()}
                <Label data-testid="redaction-box-label">
                  {box.origin === 'manual' ? 'Manual' : 'AI suggestion'}
                  {box.reason ? ` · ${box.reason}` : ''}
                </Label>
              </>
            )}
          </Box>
        )
      })}
      {draft && drag.current?.mode === 'draw' && (
        <Box
          data-testid="redaction-draft"
          style={{
            left: draft.left,
            top: draft.top,
            width: draft.width,
            height: draft.height,
            background: withAlpha(theme.colors.primary, 0.2),
            border: `2px dashed ${theme.colors.primary}`
          }}
        />
      )}
    </Layer>
  )
}

RedactionLayer.propTypes = {
  pageIndex: PropTypes.number.isRequired,
  dims: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    scale: PropTypes.number.isRequired,
    pageWidth: PropTypes.number.isRequired,
    pageHeight: PropTypes.number.isRequired
  }).isRequired,
  boxes: PropTypes.array.isRequired,
  tool: PropTypes.oneOf(['select', 'add']),
  editable: PropTypes.bool,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func,
  onCreate: PropTypes.func,
  onUpdate: PropTypes.func
}

export default RedactionLayer
